/*
 * Variant gallery filter.
 * When a colour variant is selected, show only that colour's media group and hide the rest.
 *
 * Anchor-based grouping: each colour's representative media (a variant's featured media) is an
 * anchor. A colour's group runs from its anchor position up to (but not including) the next
 * anchor position. Media whose alt contains "__all" (data-media-common) is always shown.
 * If the selected colour has no anchor, the gallery falls back to showing everything.
 *
 * Reads data attributes rendered by product-media-gallery.liquid:
 *   media-gallery[data-lumen-color-filter="true"][data-lumen-color-index]
 *   li.product__media-item[data-media-id][data-media-position]
 *     [data-anchor-for][data-anchor-color][data-media-common?]
 * and mirrors visibility to the matching thumbnail .thumbnail-list__item[data-target]
 * and to the zoom lightbox #ProductModal-<sectionId>, whose children are keyed by the bare
 * media id (the gallery uses "<sectionId>-<mediaId>"). The modal renders every media, so
 * without this mirroring scrolling inside the lightbox would reveal all colours.
 */
(function () {
  'use strict';

  var HIDE = 'lumen-vg-hidden';

  function toInt(v) {
    var n = parseInt(v, 10);
    return isNaN(n) ? null : n;
  }

  function selectedVariant(productInfo) {
    if (!productInfo) return null;
    var el = productInfo.querySelector('variant-selects [data-selected-variant]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function selectedColor(variant, colorIndex) {
    if (!variant) return null;
    if (variant.options && colorIndex != null && variant.options[colorIndex] != null) {
      return String(variant.options[colorIndex]);
    }
    return null;
  }

  function setVisible(el, show) {
    if (!el) return;
    if (show) {
      el.classList.remove(HIDE);
      el.removeAttribute('aria-hidden');
    } else {
      el.classList.add(HIDE);
      el.setAttribute('aria-hidden', 'true');
    }
  }

  function filter(gallery, variant) {
    var ul = gallery.querySelector('[id^="Slider-Gallery-"]');
    if (!ul) return;
    var items = Array.prototype.slice.call(ul.querySelectorAll(':scope > li.product__media-item'));
    if (!items.length) return;

    var thumbs = gallery.querySelector('[id^="GalleryThumbnails-"]');
    function thumbFor(li) {
      if (!thumbs || !li.dataset.mediaId) return null;
      return thumbs.querySelector('[data-target="' + li.dataset.mediaId + '"]');
    }

    // Zoom lightbox: same section id, but its children carry the bare media id.
    var sectionId = (gallery.id || '').replace(/^MediaGallery-/, '');
    var modal = sectionId ? document.getElementById('ProductModal-' + sectionId) : null;
    function modalItems() {
      if (!modal) return [];
      var content = modal.querySelector('.product-media-modal__content') || modal;
      return Array.prototype.slice.call(content.querySelectorAll(':scope > [data-media-id]'));
    }
    function bareMediaId(li) {
      var v = li.dataset.mediaId || '';
      var prefix = sectionId + '-';
      return v.indexOf(prefix) === 0 ? v.slice(prefix.length) : v;
    }

    function showAll() {
      items.forEach(function (li) {
        setVisible(li, true);
        setVisible(thumbFor(li), true);
      });
      modalItems().forEach(function (el) {
        setVisible(el, true);
      });
    }

    // start = anchor position for the selected colour (colour's representative media)
    var colorIndex = toInt(gallery.getAttribute('data-lumen-color-index'));
    var color = selectedColor(variant, colorIndex);
    var start = null;
    if (color != null) {
      for (var i = 0; i < items.length; i++) {
        if (items[i].getAttribute('data-anchor-color') === color) {
          start = toInt(items[i].getAttribute('data-media-position'));
          break;
        }
      }
    }

    // fallback: no anchor for this colour -> show everything (safer than a broken gallery)
    if (start == null) {
      console.warn('[lumen-variant-gallery] No anchor for the selected colour; showing all media.', color || '(unknown colour)');
      showAll();
      return;
    }

    // end = nearest anchor position after start (exclusive)
    var end = Infinity;
    items.forEach(function (li) {
      if (!li.hasAttribute('data-anchor-for')) return;
      var p = toInt(li.getAttribute('data-media-position'));
      if (p != null && p > start && p < end) end = p;
    });

    var startLi = null;
    var visibleMediaIds = [];
    items.forEach(function (li) {
      var pos = toInt(li.getAttribute('data-media-position'));
      var common = li.hasAttribute('data-media-common');
      var show = common || (pos != null && pos >= start && pos < end);
      setVisible(li, show);
      setVisible(thumbFor(li), show);
      if (show) visibleMediaIds.push(bareMediaId(li));
      if (pos === start) startLi = li;
    });

    // mirror the same visibility into the zoom lightbox so scrolling there stays within the colour
    var modalEls = modalItems();
    if (modalEls.length) {
      modalEls.forEach(function (el) {
        setVisible(el, visibleMediaIds.indexOf(el.getAttribute('data-media-id')) !== -1);
      });
    }

    // keep an active, visible media
    var mg = gallery.matches('media-gallery') ? gallery : gallery.closest('media-gallery');
    var active = ul.querySelector('.product__media-item.is-active');
    if (startLi && (!active || active.classList.contains(HIDE))) {
      if (mg && typeof mg.setActiveMedia === 'function') mg.setActiveMedia(startLi.dataset.mediaId, false);
    }

    // recompute slider paging + reset scroll (mobile swipe / thumbnail slider)
    var sc = ul.closest('slider-component');
    if (sc && typeof sc.resetPages === 'function') {
      try { sc.resetPages(); } catch (e) {}
    }
    if (startLi && typeof ul.scrollTo === 'function') {
      try { ul.scrollTo({ left: startLi.offsetLeft || 0 }); } catch (e) {}
    }
  }

  function galleries() {
    return document.querySelectorAll('media-gallery[data-lumen-color-filter="true"]');
  }

  function init() {
    galleries().forEach(function (g) {
      filter(g, selectedVariant(g.closest('product-info')));
    });

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
      subscribe(PUB_SUB_EVENTS.variantChange, function (e) {
        var d = e && e.data;
        if (!d) return;
        galleries().forEach(function (g) {
          var pi = g.closest('product-info');
          var sameSection =
            !pi ||
            pi.dataset.section === String(d.sectionId) ||
            pi.dataset.originalSection === String(d.sectionId);
          if (sameSection) filter(g, d.variant);
        });
      });
    } else {
      console.warn('[lumen-variant-gallery] pub/sub unavailable; variant changes will not re-filter.');
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();